import type {
  TaskMetadata,
  TaskMetadataCtaConfig,
} from 'src/types/task-and-reminder';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useCrmRedesignedEnabled} from 'src/hooks/product-flags';
import {getRedirectPath} from 'src/components/crm/lib/common';
import {TRM_PATHS} from 'src/utils/app-shell';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {generateTestId} from '@spaced-out/ui-design-system/lib/utils/qa';

import css from './task-cta-buttons.css';


export const useResolvedTaskCtas = (
  metadata?: TaskMetadata,
): Array<TaskMetadataCtaConfig> => {
  const {t} = useLabelTranslation();
  const isCrmEnabled = useCrmRedesignedEnabled();
  return React.useMemo(() => {
    if (!metadata) {
      return [];
    }
    if (metadata.cta_config?.length) {
      return metadata.cta_config;
    }

    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const ctas: Array<TaskMetadataCtaConfig> = [];

    if (metadata.candidate_details?.id) {
      const candidateId = encodeURIComponent(metadata.candidate_details.id);
      ctas.push({
        redirect_url: isCrmEnabled
          ? `${origin}/crm/candidates?selectedTab=overview&candidateId=${candidateId}&subTab=details`
          : `${origin}/people/candidates/${candidateId}/profile`,
        button_label: t('View_Candidate', 'View Candidate'),
        button_icon: 'user',
      });
    }

    if (metadata.job_details?.id) {
      const jobId = encodeURIComponent(metadata.job_details.id);
      ctas.push({
        redirect_url: `${origin}/crm/job/${jobId}/info/details`,
        button_label: t('View_Job', 'View Job'),
        button_icon: 'briefcase',
      });
    }

    return ctas;
  }, [metadata, t, isCrmEnabled]);
};

export const redirectToCta = (redirectUrl: string): void => {
  if (Object.values(TRM_PATHS).includes(redirectUrl as never)) {
    window.open(getRedirectPath(redirectUrl) ?? '', '_self');
  } else {
    window.open(redirectUrl, '_self');
  }
};

type TaskCtaButtonsProps = {
  metadata?: TaskMetadata;
  testIdBase?: string;
};

const TaskCtaButtons = ({
  metadata,
  testIdBase = 'task-inline-cta',
}: TaskCtaButtonsProps) => {
  const ctas = useResolvedTaskCtas(metadata);

  if (ctas.length === 0) {
    return null;
  }

  const stopActivation = (
    event: React.MouseEvent | React.KeyboardEvent,
  ): void => {
    event.stopPropagation();
  };

  return (
    <div className={css.ctaInline}>
      {ctas.map((cta, index) => {
        const testId = generateTestId({base: testIdBase, index});
        const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
          stopActivation(event);
          redirectToCta(cta.redirect_url);
        };
        return (
          <Button
            key={cta.redirect_url}
            type="secondary"
            size="small"
            iconLeftName={cta.button_icon}
            onClick={handleClick}
            testId={testId}
          >
            {cta.button_label}
          </Button>
        );
      })}
    </div>
  );
};

export default TaskCtaButtons;
